import * as Sentry from '@sentry/react'
import { currentSentryFeature } from './sentry'

// Slack alert rules key on `scenario`; every user-facing failure sets one.
export const SCENARIOS = Object.freeze(['export', 'report_job', 'load', 'render', 'api'])

export const JOB_STUCK_AFTER_MS = 30 * 60 * 1000
const STUCK_JOB_MAX_AGE_MS = 24 * 60 * 60 * 1000

// Paywall/limit answers, expired sessions and aborted requests are expected
// outcomes the UI already handles, not failures to alert on.
const EXPECTED_CODES = new Set([
  'LIMIT_EXCEEDED',
  'LIMITED_ACCESS',
  'UPGRADE_REQUIRED',
  'CREDIT_DEPLETED',
  'TABLE_READ_LIMIT_EXCEEDED',
  'SCRIPT_REQUIRED',
  'EXPORT_LIMIT_REACHED'
])
const EXPECTED_STATUSES = new Set([401, 402])

const reportedKeys = new Set()

export const resetReportedFailures = () => reportedKeys.clear()

export const isExpectedFailure = (error) =>
  error?.name === 'AbortError' || EXPECTED_CODES.has(error?.code) || EXPECTED_STATUSES.has(error?.status)

const toError = (error, feature, scenario) => {
  if (error instanceof Error) return error
  const detail = typeof error === 'string' ? error : error?.message
  return new Error(`${feature || 'studio'} ${scenario} failed${detail ? `: ${detail}` : ''}`)
}

// projectUuid/scriptUuid override the route's ids, e.g. for a job polled before the user navigated away.
export const reportFailure = ({ feature, scenario, error, jobId, exportType, projectUuid, scriptUuid, extra, tags, level, dedupeKey } = {}) => {
  if (isExpectedFailure(error)) return false
  if (dedupeKey) {
    if (reportedKeys.has(dedupeKey)) return false
    reportedKeys.add(dedupeKey)
  }
  const resolvedFeature = feature || currentSentryFeature()
  try {
    Sentry.withScope((scope) => {
      scope.setTag('scenario', scenario)
      if (resolvedFeature) scope.setTag('feature', resolvedFeature)
      if (jobId) scope.setTag('job_id', String(jobId))
      if (exportType) scope.setTag('export_type', exportType)
      if (projectUuid) scope.setTag('project_uuid', String(projectUuid))
      if (scriptUuid) scope.setTag('script_uuid', String(scriptUuid))
      if (error?.status) scope.setTag('http_status', String(error.status))
      if (tags) scope.setTags(tags)
      if (level) scope.setLevel(level)
      // One issue per feature + scenario, so each one reaches Slack on its own.
      scope.setFingerprint(['{{ default }}', scenario, resolvedFeature || 'none'])
      if (extra) scope.setExtras(extra)
      if (error !== undefined && !(error instanceof Error)) scope.setExtra('reported_value', error)
      Sentry.captureException(toError(error, resolvedFeature, scenario))
    })
  } catch (_) {
    // Reporting must never break the flow it is observing.
  }
  return true
}

// A 404 is a real "not found" the screens already handle; anything else is a
// failed load.
export const reportLoadFailure = (feature, error, extra) => {
  if (error?.status === 404) return false
  return reportFailure({ feature, scenario: 'load', error, extra })
}

// Once per job per session: a job still running this long has stalled
// without ever reaching a failed state the UI could show. Jobs abandoned
// for over a day are old news, not a fresh alert on every visit.
export const reportStuckJob = ({ feature, scenario = 'report_job', jobId, startedAt, thresholdMs = JOB_STUCK_AFTER_MS, now = Date.now(), exportType, projectUuid, scriptUuid, extra }) => {
  const age = now - new Date(startedAt).getTime()
  if (!jobId || !Number.isFinite(age) || age < thresholdMs || age > STUCK_JOB_MAX_AGE_MS) return false
  return reportFailure({
    feature,
    scenario,
    jobId,
    exportType,
    projectUuid,
    scriptUuid,
    error: new Error(`${feature} ${scenario} job still not done after ${Math.round(thresholdMs / 60000)} minutes`),
    tags: { reason: 'job_stuck' },
    extra: { startedAt, ...extra },
    dedupeKey: `stuck:${feature}:${scenario}:${jobId}`
  })
}
